import AppConfig from '@/AppConfig';
import { EClaimStages, EClaimType, IClaimDetails } from '@/types';
import { format } from 'date-fns/format';
import get from 'lodash.get';
import Divider from 'ui/ds/Divider';
import { twConfig } from 'ui/ds/utils/getConfig';
import { AvatarMale, Bin, ChevronRight } from 'ui/icons';
import { Button, Tag } from 'ui/mobile-ds';
import { ConditionalLink } from '../ConditionalLink';
import { redirectToPlumApp } from '@/utils';
import { useAppContext } from '@/context/AppContext';
import { MOBILE_DEEPLINKS } from '@/constants';
import useConfirm from '@/hooks/useConfirm';
import { useMutation } from '@tanstack/react-query';
import { deletePlumClaim } from '@/queries/claims.queries';
import toast from 'react-hot-toast';
import { ConfirmAlert } from '../ConfirmAlert';
import { twMerge } from 'tailwind-merge';

interface IProps {
  claimDetails: IClaimDetails;
  className?: string;
  preview?: boolean;
  onDeletion?: (claimId: string) => void;
}

// const relationshipToIconMap = {};

const ClaimsCard = ({
  claimDetails,
  className,
  preview,
  onDeletion,
}: IProps) => {
  const {
    plumClaimId: claimId = null,
    patientName,
    createdAt,
    typeOfClaim,
    typeOfCare,
    typeOfBenefit,
    uiRepresentation,
    stage,
    canDelete,
  } = claimDetails ?? {};

  const { isWebView } = useAppContext();

  const redirectIfReimbursement = () => {
    if (
      typeOfClaim === EClaimType.REIMBURSEMENT &&
      !(
        typeOfBenefit === 'health-expenses' &&
        stage === EClaimStages.DOCUMENTS_REQUESTED
      )
    ) {
      if (isWebView) {
        window?.NativeBridge?.callDeeplink?.(
          `${MOBILE_DEEPLINKS.REIMBURSEMENT_CLAIM_STATUS}${claimId}`,
        );
      } else {
        redirectToPlumApp(`/claims/${claimId}`)();
      }
    }
  };

  const canDeleteClaim =
    typeOfClaim === EClaimType.REIMBURSEMENT
      ? stage === EClaimStages.DOCUMENTS_REQUESTED
      : typeOfClaim === EClaimType.CASHLESS ||
          typeOfClaim === EClaimType.PLANNED
        ? canDelete
        : false;

  const { triggerConfirm, alertProps, setIsOpen } = useConfirm();

  const { mutate, isPending: isDeleting } = useMutation({
    mutationFn: deletePlumClaim(claimDetails?.plumClaimId).mutationFn,
    retry: false,
  });

  const handleDelete: React.MouseEventHandler<HTMLButtonElement> = async (
    event,
  ) => {
    event.stopPropagation();
    event.preventDefault();
    const proceed = await triggerConfirm({
      title: 'Delete this claim?',
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Yes, delete',
      description: 'This will permanently delete this claim',
    });
    if (proceed) {
      mutate(claimDetails.plumClaimId, {
        onSettled(data, error) {
          setIsOpen(false);
          if (error || data?.stage !== 'deleted') {
            toast.error('Failed to delete your claim');
            return;
          }
          onDeletion?.(claimDetails.plumClaimId);
        },
      });
    }
  };

  return (
    <>
      <ConditionalLink
        href={
          preview
            ? null
            : typeOfClaim === EClaimType.REIMBURSEMENT
              ? typeOfBenefit === 'health-expenses' &&
                stage === EClaimStages.DOCUMENTS_REQUESTED
                ? `${AppConfig.BASE_PATH}/claims/${claimId}/upload`
                : null
              : `${AppConfig.BASE_PATH}/claims/${claimId}`
        }
        onClick={preview ? () => {} : redirectIfReimbursement}
        className={twMerge('text-retail-text-main', className)}
      >
        <div className="border rounded-2xl bg-white border-mobile-gray-200">
          <div className="flex flex-col gap-3 md:gap-4 p-4 pb-5 md:p-6 md:pt-5">
            <div className="flex flex-col gap-5">
              <div className="flex justify-between">
                <Tag
                  prefixIcon={() => (
                    <img
                      src={uiRepresentation?.status?.icon?.svg}
                      className="w-4 h-4"
                    />
                  )}
                  text={uiRepresentation?.status?.valueCaps ?? ''}
                  intent="neutral"
                  style={{
                    backgroundColor: uiRepresentation?.status?.colorBackground,
                    color: uiRepresentation?.status?.textColor,
                  }}
                />
                <p className="text-gray-400">{claimId}</p>
              </div>
              <div className="flex gap-2">
                <AvatarMale width="40px" height="40px" />
                <div className="flex flex-col text-left gap-0.5">
                  <p className="font-chromatica text-gray-700 font-bold text-base md:text-xl leading-5">
                    {patientName}
                  </p>
                  <p className="font-normal text-sm text-mobile-text-hierarchy2">
                    Created on {format(createdAt, 'd MMM yyyy')}
                  </p>
                </div>
              </div>
            </div>
            <Divider className="border-dashed border-mobile-gray-200" />
            <div className="flex justify-between">
              <p className="text-gray-700 text-sm md:text-base">
                {typeOfClaim}
              </p>
              <p className="text-mobile-text-hierarchy2 text-sm md:text-base">
                {typeOfCare}
              </p>
            </div>
          </div>
          {preview ? null : (
            <div className="flex justify-center p-2.5 md:py-5 border-t border-mobile-gray-200 h-12 md:h-auto relative">
              <Button
                intent="tertiary"
                className="gap-1 h-auto text-sm md:text-lg"
                trailingIcon={() => (
                  <ChevronRight
                    color={get(twConfig, 'theme.colors.primary[500]')}
                  />
                )}
              >
                View details
              </Button>
              {canDeleteClaim && (
                <div className="h-8 flex justify-center items-center absolute right-5 top-1/2 transform -translate-y-1/2 gap-5">
                  <div className="h-full w-[1px] bg-mobile-gray-200" />
                  <Button
                    intent={'tertiary'}
                    prefixIcon={() => <Bin />}
                    className="w-8 h-8 min-w-fit rounded-[4px] hover:bg-gray-100 cursor-pointer"
                    onClick={handleDelete}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </ConditionalLink>
      <ConfirmAlert
        {...alertProps}
        loading={isDeleting}
        dismissAfterConfirm={false}
      />
    </>
  );
};

export default ClaimsCard;
